import { useEffect, useRef, useState, type FormEvent } from 'react';
import { passwordRules, type UserSession } from '@orbit-sync/shared-types';
import { useAuthStore } from '../auth/authStore.js';
import { useDirectoryStore } from '../chat/directoryStore.js';
import { api, errorMessage, fieldErrorsFrom, type FieldErrors } from '../lib/api/index.js';
import { describeAgent } from '../lib/userAgent.js';
import { useSettingsStore } from '../settings/settingsStore.js';
import { Avatar } from './Avatar.js';
import { Field } from './Field.js';
import { FormBanner } from './FormBanner.js';
import { Icon } from './Icon.js';

/** Pictures are cropped square and scaled to this before they leave the browser. */
const AVATAR_PX = 256;

/** Center-crops and scales an image file to a small JPEG data URL. */
async function resizeImage(file: File): Promise<string> {
  const url = URL.createObjectURL(file);

  try {
    const image = new Image();
    image.src = url;
    await image.decode();

    const side = Math.min(image.naturalWidth, image.naturalHeight);
    const canvas = document.createElement('canvas');
    canvas.width = AVATAR_PX;
    canvas.height = AVATAR_PX;

    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('This browser cannot resize images');
    }

    context.drawImage(
      image,
      (image.naturalWidth - side) / 2,
      (image.naturalHeight - side) / 2,
      side,
      side,
      0,
      0,
      AVATAR_PX,
      AVATAR_PX,
    );

    return canvas.toDataURL('image/jpeg', 0.86);
  } finally {
    URL.revokeObjectURL(url);
  }
}

type Notice = { tone: 'ok' | 'error'; text: string } | null;

/** The user's own account: picture, name, email and password. */
export function AccountDialog({ onClose }: { onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const user = useAuthStore((state) => state.user);
  const setSession = useAuthStore((state) => state.setSession);
  const remember = useDirectoryStore((state) => state.remember);
  const settings = useSettingsStore((state) => state.settings);

  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileErrors, setProfileErrors] = useState<FieldErrors>({});
  const [profileNotice, setProfileNotice] = useState<Notice>(null);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<FieldErrors>({});
  const [passwordNotice, setPasswordNotice] = useState<Notice>(null);

  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  useEffect(() => {
    ref.current?.showModal();
  }, []);

  if (!user) {
    return null;
  }

  const fromDirectory = user.authSource === 'ldap';
  const emailChanged = email.trim().toLowerCase() !== user.email;
  const profileChanged = displayName.trim() !== user.displayName || emailChanged;
  const rules = newPassword ? passwordRules(newPassword, settings) : [];

  function publish(next: typeof user): void {
    if (next) {
      remember([
        { id: next.id, displayName: next.displayName, avatarUpdatedAt: next.avatarUpdatedAt },
      ]);
    }
  }

  async function saveProfile(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setProfileBusy(true);
    setProfileErrors({});
    setProfileNotice(null);

    try {
      const session = await api.auth.updateAccount({
        ...(displayName.trim() !== user?.displayName ? { displayName: displayName.trim() } : {}),
        ...(emailChanged ? { email: email.trim(), currentPassword: confirmPassword } : {}),
      });
      setSession(session);
      publish(session.user);
      setConfirmPassword('');
      setProfileNotice({ tone: 'ok', text: 'Saved.' });
    } catch (error) {
      setProfileErrors(fieldErrorsFrom(error));
      setProfileNotice({ tone: 'error', text: errorMessage(error) });
    } finally {
      setProfileBusy(false);
    }
  }

  async function savePassword(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setPasswordErrors({});
    setPasswordNotice(null);

    if (newPassword !== repeatPassword) {
      setPasswordErrors({ repeatPassword: 'The two passwords do not match' });
      return;
    }

    setPasswordBusy(true);

    try {
      setSession(await api.auth.changePassword({ currentPassword, newPassword }));
      setCurrentPassword('');
      setNewPassword('');
      setRepeatPassword('');
      setPasswordNotice({ tone: 'ok', text: 'Password changed.' });
    } catch (error) {
      setPasswordErrors(fieldErrorsFrom(error));
      setPasswordNotice({ tone: 'error', text: errorMessage(error) });
    } finally {
      setPasswordBusy(false);
    }
  }

  async function changeAvatar(file: File | undefined): Promise<void> {
    if (!file) {
      return;
    }

    setAvatarBusy(true);
    setAvatarError(null);

    try {
      const updated = await api.auth.setAvatar(await resizeImage(file));
      useAuthStore.setState({ user: updated });
      publish(updated);
    } catch (error) {
      setAvatarError(
        error instanceof Error && !('status' in error)
          ? 'That image could not be read'
          : errorMessage(error),
      );
    } finally {
      setAvatarBusy(false);
    }
  }

  async function removeAvatar(): Promise<void> {
    setAvatarBusy(true);
    setAvatarError(null);

    try {
      const updated = await api.auth.removeAvatar();
      useAuthStore.setState({ user: updated });
      publish(updated);
    } catch (error) {
      setAvatarError(errorMessage(error));
    } finally {
      setAvatarBusy(false);
    }
  }

  return (
    <dialog
      ref={ref}
      className="modal modal--wide"
      onClose={onClose}
      aria-labelledby="account-title"
    >
      <header className="modal-head">
        <h2 id="account-title">Your account</h2>
        <button type="button" className="btn btn--icon" onClick={onClose} aria-label="Close">
          <Icon name="x" size={17} />
        </button>
      </header>

      <div className="modal-scroll account-dialog">
        <section className="account-avatar">
          <Avatar name={user.displayName} id={user.id} size="xl" />
          <div className="account-avatar-actions">
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              disabled={avatarBusy}
              onClick={() => fileRef.current?.click()}
            >
              {avatarBusy ? <span className="btn-spinner" /> : <Icon name="upload" size={13} />}
              {user.avatarUpdatedAt ? 'Change picture' : 'Add a picture'}
            </button>
            {user.avatarUpdatedAt ? (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={avatarBusy}
                onClick={() => void removeAvatar()}
              >
                Remove
              </button>
            ) : null}
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                void changeAvatar(file);
              }}
            />
            {avatarError ? <p className="field-error">{avatarError}</p> : null}
          </div>
        </section>

        <form className="account-section" onSubmit={(event) => void saveProfile(event)}>
          <h3>Profile</h3>
          {fromDirectory ? (
            <p className="field-hint">
              Your name and email come from your organisation’s directory and are changed there.
            </p>
          ) : null}
          <Field
            label="Display name"
            name="displayName"
            autoComplete="name"
            maxLength={settings.maxDisplayNameLength}
            value={displayName}
            disabled={fromDirectory}
            onChange={(event) => setDisplayName(event.target.value)}
            error={profileErrors.displayName}
          />
          <Field
            label="Email"
            name="email"
            type="email"
            autoComplete="username"
            value={email}
            disabled={fromDirectory}
            onChange={(event) => setEmail(event.target.value)}
            error={profileErrors.email}
          />
          {emailChanged ? (
            <Field
              label="Current password"
              name="confirmPassword"
              type="password"
              autoComplete="current-password"
              hint="Needed to change the email you sign in with."
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              error={profileErrors.currentPassword}
            />
          ) : null}
          {profileNotice ? (
            profileNotice.tone === 'error' ? (
              <FormBanner message={profileNotice.text} />
            ) : (
              <p className="account-ok">
                <Icon name="check" size={13} />
                {profileNotice.text}
              </p>
            )
          ) : null}
          <button
            type="submit"
            className="btn btn--primary"
            disabled={profileBusy || !profileChanged || (emailChanged && !confirmPassword)}
          >
            {profileBusy ? <span className="btn-spinner" /> : null}
            Save profile
          </button>
        </form>

        {fromDirectory ? (
          <section className="account-section">
            <h3>Password</h3>
            <p className="field-hint">
              You sign in with your directory password. Change it the way your organisation changes
              passwords - for example Ctrl+Alt+Del on a work PC.
            </p>
          </section>
        ) : (
          <form className="account-section" onSubmit={(event) => void savePassword(event)}>
            <h3>Password</h3>
            {/* Lets a password manager file the new password under the right account. */}
            <input type="email" autoComplete="username" value={user.email} readOnly hidden />
            <Field
              label="Current password"
              name="currentPassword"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              error={passwordErrors.currentPassword}
            />
            <Field
              label="New password"
              name="newPassword"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              error={passwordErrors.newPassword}
            />
            {rules.length > 0 ? (
              <ul className="password-rules">
                {rules.map((rule) => (
                  <li
                    key={rule.id}
                    className={rule.met ? 'password-rule password-rule--met' : 'password-rule'}
                  >
                    <Icon name={rule.met ? 'check' : 'x'} size={12} />
                    {rule.label}
                  </li>
                ))}
              </ul>
            ) : null}
            <Field
              label="Repeat new password"
              name="repeatPassword"
              type="password"
              autoComplete="new-password"
              value={repeatPassword}
              onChange={(event) => setRepeatPassword(event.target.value)}
              error={passwordErrors.repeatPassword}
            />
            {passwordNotice ? (
              passwordNotice.tone === 'error' ? (
                <FormBanner message={passwordNotice.text} />
              ) : (
                <p className="account-ok">
                  <Icon name="check" size={13} />
                  {passwordNotice.text}
                </p>
              )
            ) : null}
            <button
              type="submit"
              className="btn btn--primary"
              disabled={
                passwordBusy || !currentPassword || !newPassword || rules.some((rule) => !rule.met)
              }
            >
              {passwordBusy ? <span className="btn-spinner" /> : null}
              Change password
            </button>
            <p className="field-hint">Changing it signs you out on your other devices.</p>
          </form>
        )}

        <AccountSessions />
      </div>
    </dialog>
  );
}

/** Where this account is signed in, with "sign out everywhere else". */
function AccountSessions() {
  const [sessions, setSessions] = useState<UserSession[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    api.auth
      .listSessions(controller.signal)
      .then(setSessions)
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) {
          setError(errorMessage(cause));
        }
      });

    return () => controller.abort();
  }, [nonce]);

  async function end(action: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(null);

    try {
      await action();
      setNonce((value) => value + 1);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  const others = (sessions ?? []).filter((session) => !session.current);

  return (
    <section className="account-section">
      <h3>Where you are signed in</h3>
      <FormBanner message={error} />
      <ul className="session-list">
        {(sessions ?? []).map((session) => (
          <li key={session.id} className="session-row">
            <span className="session-body">
              <strong className="truncate">
                {describeAgent(session.userAgent)}
                {session.current ? ' · this device' : ''}
              </strong>
              <span className="field-hint">
                {session.ip ?? 'unknown address'} · last active{' '}
                {new Date(session.lastUsedAt).toLocaleString()}
              </span>
            </span>
            {session.current ? null : (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={busy}
                onClick={() => void end(() => api.auth.endSession(session.id))}
              >
                Sign out
              </button>
            )}
          </li>
        ))}
      </ul>
      {others.length > 0 ? (
        <button
          type="button"
          className="btn btn--secondary"
          disabled={busy}
          onClick={() => void end(() => api.auth.endOtherSessions())}
        >
          Sign out everywhere else
        </button>
      ) : null}
    </section>
  );
}
